import { Fragment, useEffect, useMemo, useState } from 'react';
import type { BackendCapabilities, ContainerSummary, NetworkSummary } from '@hub/shared';
import { api } from '../../../api/client';
import { ActionButton, Alert, Collapsible, EmptyRow, Loading, useFlash } from '../../../components/ui';
import {
  CheckField, DOCKER_BASE, DataTable, Field, FormActions, FormGrid, TabToolbar,
  enc, errMsg, useTabResource, useToggleSet, type DockerCache,
} from './shared';

interface CreateNetworkRequest {
  name: string;
  driver?: string;
  subnet?: string;
  gateway?: string;
  internal: boolean;
}

export function NetworksTab({ caps, cache, refreshToken }: {
  caps: BackendCapabilities;
  cache: DockerCache;
  refreshToken: number;
}) {
  const { data, loading, error, reload } = useTabResource<NetworkSummary[], { networks: NetworkSummary[] }>({
    cache, tab: 'networks', path: `${DOCKER_BASE}/networks`, extract: (r) => r.networks, refreshToken,
  });
  const { show, node: flashNode } = useFlash();
  const [open, toggleOpen] = useToggleSet();
  const [formOpen, setFormOpen] = useState(false);

  // The connect picker needs a container list; the containers tab may not have run yet.
  const containers = cache.read<ContainerSummary[]>('containers').data;
  useEffect(() => {
    if (!caps.networkConnect || containers) return;
    api.get<{ containers: ContainerSummary[] }>(`${DOCKER_BASE}/containers`)
      .then((r) => cache.write('containers', r.containers))
      .catch(() => {});
  }, [caps.networkConnect, containers, cache]);

  const list = useMemo(() => data ?? [], [data]);

  const toolbar = (
    <TabToolbar onRefresh={() => { void reload(true); }} flash={flashNode}>
      <CreateNetworkForm open={formOpen} onToggle={() => setFormOpen((v) => !v)} onCreated={() => reload(true)} />
    </TabToolbar>
  );

  if (loading && !data) return <>{toolbar}<Loading /></>;
  if (error && !data) return <>{toolbar}<Alert tone="error">{error}</Alert></>;

  return (
    <>
      {toolbar}
      <DataTable headers={['Name', 'ID', 'Driver', 'Scope', 'Subnet', 'Containers', 'Actions']}>
        {list.length === 0 && <EmptyRow colSpan={7}>No networks.</EmptyRow>}
        {list.map((n) => {
          const canDelete = !n.builtIn && n.containers.length === 0;
          return (
            <Fragment key={n.id}>
              <tr>
                <td className="mono" style={{ color: 'var(--ha-text-primary)' }}>
                  {n.name}{n.builtIn && <> <span className="pill pill-muted">built-in</span></>}
                </td>
                <td className="mono muted">{n.id.substring(0, 12)}</td>
                <td className="muted">{n.driver}</td>
                <td className="muted">{n.scope}</td>
                <td className="mono muted">{n.subnet || '–'}</td>
                <td className="muted">{n.containers.length}</td>
                <td style={{ display: 'flex', gap: '0.25rem' }}>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => toggleOpen(n.id)}>Containers</button>
                  {canDelete ? (
                    <ActionButton
                      danger
                      busyLabel="Deleting…"
                      confirm="Delete this network?"
                      onClick={async () => {
                        try {
                          await api.delete(`${DOCKER_BASE}/networks/${enc(n.id)}`);
                        } catch (err) {
                          show(errMsg(err), 'error');
                          throw err;
                        }
                        await reload(true);
                      }}
                    >Delete</ActionButton>
                  ) : (
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      disabled
                      title={n.builtIn ? 'Built-in network' : 'Containers still attached'}
                    >Delete</button>
                  )}
                </td>
              </tr>
              {open.has(n.id) && (
                <tr>
                  <td colSpan={7} style={{ padding: '0 0.4rem 0.75rem' }}>
                    <NetworkPanel
                      network={n}
                      containers={containers ?? []}
                      canConnect={caps.networkConnect}
                      onChanged={() => reload(true)}
                      onError={(m) => show(m, 'error')}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </DataTable>
    </>
  );
}

function NetworkPanel({ network, containers, canConnect, onChanged, onError }: {
  network: NetworkSummary;
  containers: ContainerSummary[];
  canConnect: boolean;
  onChanged: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [pick, setPick] = useState('');
  // Containers may arrive after the panel opened, so fall back to the first entry.
  const target = pick || containers[0]?.id || '';

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      onError(errMsg(err));
      throw err;
    }
    await onChanged();
  };

  return (
    <div style={{
      background: 'var(--ha-bg-base)', border: '1px solid var(--ha-border)',
      borderRadius: '0.375rem', padding: '0.75rem',
    }}>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {network.containers.length === 0 && (
          <li className="muted" style={{ fontSize: '0.78rem' }}>No containers attached.</li>
        )}
        {network.containers.map((c) => (
          <li key={c.id} style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.2rem 0', color: 'var(--ha-text-muted)', fontSize: '0.78rem',
          }}>
            <span className="mono" style={{ color: 'var(--ha-text-body)' }}>{c.name}</span>
            <span className="mono">{c.ipv4 || ''}</span>
            {canConnect && (
              <ActionButton onClick={() => run(() =>
                api.post(`${DOCKER_BASE}/networks/${enc(network.id)}/disconnect`, { containerId: c.id }))}
              >Disconnect</ActionButton>
            )}
          </li>
        ))}
      </ul>
      {canConnect && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
          <select
            className="form-input"
            style={{ width: '16rem', padding: '0.2rem 0.5rem', fontSize: '0.78rem' }}
            value={target}
            onChange={(e) => setPick(e.target.value)}
          >
            {containers.map((c) => (
              <option key={c.id} value={c.id}>{c.names[0] || c.id.substring(0, 12)}</option>
            ))}
          </select>
          <ActionButton
            disabled={!target}
            onClick={() => run(() =>
              api.post(`${DOCKER_BASE}/networks/${enc(network.id)}/connect`, { containerId: target }))}
          >Connect</ActionButton>
        </div>
      )}
    </div>
  );
}

function CreateNetworkForm({ open, onToggle, onCreated }: {
  open: boolean;
  onToggle: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [driver, setDriver] = useState('');
  const [subnet, setSubnet] = useState('');
  const [gateway, setGateway] = useState('');
  const [internal, setInternal] = useState(false);
  const { show, node } = useFlash();

  const submit = async () => {
    if (!name.trim()) {
      show('Name is required', 'error');
      throw new Error('Name is required');
    }
    const spec: CreateNetworkRequest = { name: name.trim(), internal };
    if (driver.trim()) spec.driver = driver.trim();
    if (subnet.trim()) spec.subnet = subnet.trim();
    if (gateway.trim()) spec.gateway = gateway.trim();
    try {
      await api.post(`${DOCKER_BASE}/networks`, spec);
    } catch (err) {
      show(errMsg(err), 'error');
      throw err;
    }
    await onCreated();
  };

  return (
    <Collapsible label="＋ Create network" open={open} onToggle={onToggle}>
      <FormGrid min={180}>
        <Field label="Name"><input className="form-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Driver"><input className="form-input" placeholder="bridge" value={driver} onChange={(e) => setDriver(e.target.value)} /></Field>
        <Field label="Subnet"><input className="form-input" placeholder="172.28.0.0/16" value={subnet} onChange={(e) => setSubnet(e.target.value)} /></Field>
        <Field label="Gateway"><input className="form-input" value={gateway} onChange={(e) => setGateway(e.target.value)} /></Field>
        <CheckField label="Internal" checked={internal} onChange={setInternal} />
      </FormGrid>
      <FormActions>
        <ActionButton className="btn-primary" busyLabel="Creating…" onClick={submit}>Create</ActionButton>
        {node}
      </FormActions>
    </Collapsible>
  );
}
